import type { QueryClient } from '@tanstack/react-query';
import type { DmNotes, LiveDmNotes } from '../services/dmNotes';
import { DM_NOTES_KEY, DM_NOTES_LIVE_KEY, PATIENT_CARD_KEY } from './queryKeys';

// Exact: a prefix match would also refetch the live notes query, which refreshes the card again.
function refreshPatientCard(queryClient: QueryClient, patientId: string): void {
    void queryClient.invalidateQueries({ queryKey: PATIENT_CARD_KEY(patientId), exact: true });
}

// Awaiting the cancel matters: a cancelled fetch reverts its query later and would undo the write.
export async function storeLiveDmNotes(
    queryClient: QueryClient,
    patientId: string,
    live: LiveDmNotes,
    signal: AbortSignal
): Promise<void> {
    await queryClient.cancelQueries({ queryKey: DM_NOTES_KEY(patientId), exact: true });
    if (signal.aborted) return;
    queryClient.setQueryData<DmNotes>(DM_NOTES_KEY(patientId), { notes: live.notes });
    if (live.changed) refreshPatientCard(queryClient, patientId);
}

// Cancels in-flight reads first: a live read sent before the save would return the old text.
export async function storeSavedDmNotes(
    queryClient: QueryClient,
    patientId: string,
    notes: string
): Promise<void> {
    await Promise.all([
        queryClient.cancelQueries({ queryKey: DM_NOTES_KEY(patientId), exact: true }),
        queryClient.cancelQueries({ queryKey: DM_NOTES_LIVE_KEY(patientId), exact: true })
    ]);
    queryClient.setQueryData<DmNotes>(DM_NOTES_KEY(patientId), { notes });
    queryClient.setQueryData<LiveDmNotes>(DM_NOTES_LIVE_KEY(patientId), { notes, changed: false });
    refreshPatientCard(queryClient, patientId);
}
